import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  ArrowClockwise,
  Bell,
  CaretDown,
  FloppyDisk,
  PaperPlaneTilt,
  Trash,
  UploadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const COLORS = [
  ["header_bg", "Header band"],
  ["header_text", "Header text"],
  ["page_bg", "Outside the card"],
  ["card_bg", "Card"],
  ["text", "Body text"],
  ["muted", "Quiet text"],
  ["accent", "Links & buttons"],
];

const FONT_LABELS = {
  sans: "Arial (safest)",
  modern: "Helvetica Neue",
  serif: "Georgia (serif)",
  mono: "Courier (typewriter)",
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    {children}
    {hint && <span className="mt-1 block text-xs text-zinc-600">{hint}</span>}
  </label>
);

const Swatch = ({ id, label, value, onChange }) => (
  <div className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2">
    <input
      type="color"
      data-testid={`email-color-${id}`}
      value={value || "#000000"}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-10 cursor-pointer border-0 bg-transparent p-0"
    />
    <span className="min-w-0 flex-1 text-xs text-zinc-400">{label}</span>
    <span className="font-mono-data text-[10px] uppercase text-zinc-600">
      {value}
    </span>
  </div>
);

const Panel = ({ title, sub, testId, open, onToggle, right, children }) => (
  <section
    className="rounded-lg border border-zinc-800 bg-zinc-900"
    data-testid={testId}
  >
    <div className="flex w-full items-center gap-3 p-5">
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs font-bold uppercase tracking-widest text-zinc-300">
            {title}
          </span>
          {sub && (
            <span className="mt-1 block text-xs text-zinc-600">{sub}</span>
          )}
        </span>
        <CaretDown
          size={14}
          weight="bold"
          className={`ml-auto shrink-0 text-zinc-500 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {right}
    </div>
    {open && (
      <div className="space-y-4 border-t border-zinc-800 p-5">{children}</div>
    )}
  </section>
);

export default function EmailDesign() {
  const [data, setData] = useState(null);
  const [design, setDesign] = useState(null);
  const [notices, setNotices] = useState({});
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState("brand");
  const [testTo, setTestTo] = useState("");
  const [outbound, setOutboundState] = useState(true);
  const fileRef = useRef(null);

  const setOutbound = async (enabled) => {
    setBusy("outbound");
    try {
      const { data: d } = await api.put("/settings/email-design/outbound", { enabled });
      setOutboundState(d.outbound);
      toast.success(d.outbound ? "Sending is back on" : "All sending paused — nothing automatic will go out");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change the switch");
    }
    setBusy("");
  };

  useEffect(() => {
    api
      .get("/settings/email-design")
      .then(({ data: d }) => {
        setData(d);
        setOutboundState(d.outbound !== false);
        setDesign((c) => c ?? d.design);
        setNotices((c) => (Object.keys(c).length ? c : d.notices));
      })
      .catch(() => toast.error("Could not load the email design"));
  }, []);

  const draft = useCallback(() => ({ design, notices }), [design, notices]);

  const refreshPreview = useCallback(async () => {
    if (!design) return;
    try {
      const { data: d } = await api.post(
        "/settings/email-design/preview",
        draft(),
      );
      setPreview(d.html);
    } catch {
      setPreview("");
    }
  }, [design, draft]);

  useEffect(() => {
    const t = setTimeout(refreshPreview, 400);
    return () => clearTimeout(t);
  }, [refreshPreview]);

  if (!design || !data)
    return (
      <div className="p-8" data-testid="email-design-loading">
        <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
      </div>
    );

  const set = (key, value) => setDesign((c) => ({ ...c, [key]: value }));
  const rule = (key) =>
    notices[key] || { enabled: true, subject: "", note: "" };
  const setRule = (key, patch) =>
    setNotices((c) => ({ ...c, [key]: { ...rule(key), ...patch } }));

  const save = async () => {
    setBusy("save");
    try {
      const { data: d } = await api.put("/settings/email-design", draft());
      setDesign(d.design);
      setNotices(d.notices);
      toast.success("Saved — every email now uses this design");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy("");
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const { data: d } = await api.post("/settings/email-design/test", null, {
        params: testTo.trim() ? { to: testTo.trim() } : {},
      });
      toast.success(`Test email sent to ${d.to}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send the test",
      );
    }
    setBusy("");
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: d } = await api.post("/settings/email-design/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo", d.path);
      toast.success("Logo uploaded — press Save to use it");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not upload that image",
      );
    }
    setBusy("");
  };

  const groups = [
    ["customer", "Customer emails"],
    ["staff", "Office alerts"],
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="email-design-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            Email Design & Notifications
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            One layout for every email you send, and a switch for each automatic
            message
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label
            data-testid="outbound-master-toggle"
            className={`flex h-11 cursor-pointer items-center gap-3 rounded-lg border px-4 text-xs font-bold uppercase tracking-widest transition-colors duration-150 ${
              outbound
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/60 bg-red-500/15 text-red-200"
            }`}
            title="Master switch: emails, texts and office alerts. Off = nothing automatic leaves the shop."
          >
            <Switch
              checked={outbound}
              disabled={busy === "outbound"}
              onCheckedChange={setOutbound}
              data-testid="outbound-master-switch"
            />
            {outbound ? "All sending on" : "All sending paused"}
          </label>
          <Input
            data-testid="email-design-test-to"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Send the test to…"
            className="h-11 w-56 border-zinc-800 bg-zinc-950 text-sm"
          />
          <Button
            data-testid="email-design-test"
            variant="outline"
            disabled={busy === "test"}
            onClick={sendTest}
            className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
          >
            <PaperPlaneTilt size={15} weight="bold" className="mr-2" />
            {busy === "test" ? "Sending…" : "Send me a test"}
          </Button>
          <Button
            data-testid="email-design-save"
            disabled={busy === "save"}
            onClick={save}
            className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <FloppyDisk size={15} weight="bold" className="mr-2" />
            {busy === "save" ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {!outbound && (
        <div
          data-testid="outbound-paused-banner"
          className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100"
        >
          All automatic emails, texts and office alerts are paused. Quotes, invoices, reminders
          and route texts will be logged as “paused” instead of sent until you switch this back on.
        </div>
      )}

      <div className="mt-8 grid gap-6 xl:grid-cols-[1fr_1fr]">
        <div className="space-y-4">
          <Panel
            title="Logo & header"
            sub="What sits at the top of every email"
            testId="email-brand"
            open={open === "brand"}
            onToggle={() => setOpen(open === "brand" ? "" : "brand")}
            right={
              <span className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Show logo
                </span>
                <Switch
                  data-testid="email-show-logo"
                  checked={design.show_logo}
                  onCheckedChange={(v) => set("show_logo", v)}
                />
              </span>
            }
          >
            <div className="flex items-center gap-4">
              <div
                className="flex h-20 w-40 items-center justify-center border border-zinc-800"
                style={{ backgroundColor: design.header_bg }}
                data-testid="email-logo-box"
              >
                {design.logo || design.use_company_logo ? (
                  <img
                    src={`${API}/public/email-logo${design.logo_white ? "?tone=white" : ""}`}
                    alt="Email logo"
                    data-testid="email-logo-preview"
                    className="max-h-16 max-w-[140px] object-contain"
                  />
                ) : (
                  <span
                    className="text-[10px] uppercase tracking-widest"
                    style={{ color: design.header_text }}
                  >
                    Shop name
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
                <Button
                  data-testid="email-logo-upload"
                  variant="outline"
                  disabled={busy === "logo"}
                  onClick={() => fileRef.current?.click()}
                  className="h-10 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <UploadSimple size={14} weight="bold" className="mr-2" />
                  {busy === "logo" ? "Uploading…" : "Upload a logo"}
                </Button>
                {design.logo && (
                  <Button
                    data-testid="email-logo-clear"
                    variant="ghost"
                    onClick={() => set("logo", "")}
                    className="h-9 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
                  >
                    <Trash size={13} weight="bold" className="mr-2" /> Use
                    company logo
                  </Button>
                )}
              </div>
            </div>
            <label className="flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  White logo for the dark header
                </span>
                <span className="mt-0.5 block text-xs text-zinc-600">
                  Recolors your logo white so it shows on the header band — no
                  second file needed
                </span>
              </span>
              <Switch
                data-testid="email-logo-white"
                checked={!!design.logo_white}
                onCheckedChange={(v) => set("logo_white", v)}
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Logo height (px)">
                <Input
                  data-testid="email-logo-height"
                  type="number"
                  min={20}
                  max={90}
                  value={design.logo_height}
                  onChange={(e) =>
                    set("logo_height", Number(e.target.value) || 40)
                  }
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Header alignment">
                <Select
                  value={design.align}
                  onValueChange={(v) => set("align", v)}
                >
                  <SelectTrigger
                    data-testid="email-align"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Centre</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Panel>

          <Panel
            title="Colors & type"
            sub="Card, text, links and the font family"
            testId="email-colors"
            open={open === "colors"}
            onToggle={() => setOpen(open === "colors" ? "" : "colors")}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              {COLORS.map(([key, label]) => (
                <Swatch
                  key={key}
                  id={key}
                  label={label}
                  value={design[key]}
                  onChange={(v) => set(key, v)}
                />
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Font">
                <Select
                  value={design.font}
                  onValueChange={(v) => set("font", v)}
                >
                  <SelectTrigger
                    data-testid="email-font"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(data.fonts || []).map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {FONT_LABELS[f.key] || f.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Corner rounding (px)" hint="0 keeps square corners">
                <Input
                  data-testid="email-corner"
                  type="number"
                  min={0}
                  max={24}
                  value={design.corner}
                  onChange={(e) => set("corner", Number(e.target.value) || 0)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
          </Panel>

          <Panel
            title="Signature & footer"
            sub="How every email signs off"
            testId="email-footer"
            open={open === "footer"}
            onToggle={() => setOpen(open === "footer" ? "" : "footer")}
            right={
              <span className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Sign off
                </span>
                <Switch
                  data-testid="email-show-signature"
                  checked={design.show_signature}
                  onCheckedChange={(v) => set("show_signature", v)}
                />
              </span>
            }
          >
            <Field label="Signature line">
              <Input
                data-testid="email-signature"
                value={design.signature}
                onChange={(e) => set("signature", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
              <span className="text-xs text-zinc-400">
                Show the support line in the footer of every email
              </span>
              <Switch
                data-testid="email-show-help"
                checked={design.show_help !== false}
                onCheckedChange={(v) => set("show_help", v)}
              />
            </div>
            {design.show_help !== false && (
              <div className="mt-3 space-y-3">
                <Field
                  label="Footer tagline"
                  hint="One line under the shop name, e.g. what you make"
                >
                  <Input
                    data-testid="email-tagline"
                    value={design.tagline || ""}
                    onChange={(e) => set("tagline", e.target.value)}
                    placeholder="Countertops fabricated and installed by our own crew."
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Support email">
                    <Input
                      data-testid="email-support-email"
                      value={design.support_email || ""}
                      onChange={(e) => set("support_email", e.target.value)}
                      placeholder="help@shop.com"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </Field>
                  <Field label="Support phone">
                    <Input
                      data-testid="email-support-phone"
                      value={design.support_phone || ""}
                      onChange={(e) => set("support_phone", e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </Field>
                  <Field label="Hours">
                    <Input
                      data-testid="email-support-hours"
                      value={design.support_hours || ""}
                      onChange={(e) => set("support_hours", e.target.value)}
                      placeholder="Mon–Fri 8–5"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </Field>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
              <span className="text-xs text-zinc-400">
                Show social icons in the footer
              </span>
              <Switch
                data-testid="email-show-socials"
                checked={design.show_socials !== false}
                onCheckedChange={(v) => set("show_socials", v)}
              />
            </div>
            {design.show_socials !== false && (
              <div className="mt-3 space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="X / Twitter URL">
                    <Input
                      data-testid="email-twitter"
                      value={design.twitter || ""}
                      onChange={(e) => set("twitter", e.target.value)}
                      placeholder="https://x.com/yourshop"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </Field>
                  <Field label="LinkedIn URL">
                    <Input
                      data-testid="email-linkedin"
                      value={design.linkedin || ""}
                      onChange={(e) => set("linkedin", e.target.value)}
                      placeholder="https://linkedin.com/company/yourshop"
                      className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                    />
                  </Field>
                </div>
                <p className="text-[11px] text-zinc-600">
                  Instagram &amp; Facebook are pulled from Settings → Public
                  Website.
                </p>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
              <span className="text-xs text-zinc-400">
                Show Privacy Policy &amp; Terms links in the footer
              </span>
              <Switch
                data-testid="email-show-legal"
                checked={design.show_legal_links !== false}
                onCheckedChange={(v) => set("show_legal_links", v)}
              />
            </div>
            <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
              <span className="text-xs text-zinc-400">
                Show the &quot;we never ask for your password&quot; note
              </span>
              <Switch
                data-testid="email-show-safety"
                checked={design.show_safety_note}
                onCheckedChange={(v) => set("show_safety_note", v)}
              />
            </div>
            {design.show_safety_note && (
              <Field label="Safety note wording">
                <Input
                  data-testid="email-safety-note"
                  value={design.safety_note}
                  onChange={(e) => set("safety_note", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            )}
          </Panel>

          {groups.map(([who, label]) => (
            <Panel
              key={who}
              title={label}
              sub={
                who === "customer"
                  ? "Automatic emails that go to your customers"
                  : "Alerts that go to the office — who gets them is set in User Groups"
              }
              testId={`email-notices-${who}`}
              open={open === who}
              onToggle={() => setOpen(open === who ? "" : who)}
              right={
                <Bell
                  size={15}
                  weight="bold"
                  className="shrink-0 text-zinc-600"
                />
              }
            >
              {(data.catalog || [])
                .filter((n) => n.who === who)
                .map((n) => {
                  const r = rule(n.key);
                  return (
                    <div
                      key={n.key}
                      data-testid={`notice-${n.key}`}
                      className="rounded-md border border-zinc-800 bg-zinc-950 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-zinc-200">
                            {n.label}
                          </div>
                          <div className="mt-0.5 text-xs text-zinc-600">
                            {n.detail}
                          </div>
                        </div>
                        <Switch
                          data-testid={`notice-${n.key}-enabled`}
                          checked={r.enabled !== false}
                          onCheckedChange={(v) =>
                            setRule(n.key, { enabled: v })
                          }
                        />
                      </div>
                      {r.enabled !== false && (
                        <div className="mt-3 space-y-2">
                          <Input
                            data-testid={`notice-${n.key}-subject`}
                            value={r.subject}
                            onChange={(e) =>
                              setRule(n.key, { subject: e.target.value })
                            }
                            placeholder="Subject line — leave blank to keep ours"
                            className="h-10 border-zinc-800 bg-zinc-900 text-sm"
                          />
                          <Textarea
                            data-testid={`notice-${n.key}-note`}
                            rows={2}
                            value={r.note}
                            onChange={(e) =>
                              setRule(n.key, { note: e.target.value })
                            }
                            placeholder="Extra line to add to this email (optional)"
                            className="border-zinc-800 bg-zinc-900 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
            </Panel>
          ))}
        </div>

        <div className="xl:sticky xl:top-6 xl:self-start">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400">
              Live preview
            </h2>
            <Button
              data-testid="email-preview-refresh"
              variant="ghost"
              onClick={refreshPreview}
              className="h-9 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
            >
              <ArrowClockwise size={13} weight="bold" className="mr-2" />{" "}
              Refresh
            </Button>
          </div>
          <div
            data-testid="email-preview"
            className="mt-3 max-h-[78vh] overflow-auto rounded-lg border border-zinc-800"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(preview, {
                ADD_ATTR: ["height", "align", "cellpadding", "cellspacing"],
              }),
            }}
          />
        </div>
      </div>
    </div>
  );
}
